/**
 * Broadcast / Discount Ninja drawer-cart integration.
 * Uses only Discount Ninja's native drawer-cart events and content.
 */
(function () {
  'use strict';

  var notifyQueued = false;
  var redrawQueued = false;

  function drawerExists() {
    return Boolean(document.querySelector(
      'cart-drawer[la-dn-drawercart-root], cart-drawer.limoniapps-discountninja-drawercart-root'
    ));
  }

  function notifyDiscountNinja() {
    if (notifyQueued) return;
    notifyQueued = true;

    // One frame is sufficient for custom elements to reconnect before
    // Discount Ninja scans the drawer markup.
    window.requestAnimationFrame(function () {
      notifyQueued = false;
      if (!drawerExists()) return;
      document.dispatchEvent(
        new CustomEvent('limoniapps:discountninja:drawercart:opened')
      );
    });
  }

  function ninjaItems(cart) {
    if (!cart) return [];
    return cart.items || cart.Items || cart.line_items || cart.LineItems || [];
  }

  function itemQuantity(item) {
    var line = item && (item.Line || item.line);
    return Number(
      (item && (item.quantity || item.Quantity)) ||
      (line && (line.quantity || line.Quantity)) ||
      0
    );
  }

  function cartMatchesDrawer(cart) {
    var items = ninjaItems(cart);
    if (!items.length) return false;

    var inputs = document.querySelectorAll(
      'cart-drawer [data-la-dn-cart-item-row] input[name="updates[]"]'
    );
    if (items.length !== inputs.length) return false;

    var ninjaQuantity = items.reduce(function (total, item) {
      return total + itemQuantity(item);
    }, 0);
    var drawerQuantity = Array.prototype.reduce.call(inputs, function (total, input) {
      return total + (parseInt(input.value, 10) || 0);
    }, 0);

    return ninjaQuantity === drawerQuantity;
  }

  /**
   * Refresh the ENTIRE drawer content (title, items, upsells, notes, totals,
   * checkout buttons) through the Section Rendering API while keeping the
   * <cart-drawer> and its sliding panel in place, so it never closes,
   * flashes or replays the slide-in animation.
   */
  function refreshCartDrawer() {
    var wrapper = document.querySelector('[data-section-type="cart-drawer"]');
    var sectionId = wrapper && wrapper.getAttribute('data-section-id');
    var drawer = wrapper && wrapper.querySelector('cart-drawer');
    var inner = drawer && drawer.querySelector('[data-cart-drawer-inner]');
    if (!sectionId || !drawer || !inner) return Promise.resolve();

    // Must NOT use /cart: the drawer section renders empty on the cart template.
    var rootUrl = (window.theme && theme.routes && theme.routes.root_url) || '/';
    return fetch(rootUrl + (rootUrl.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + sectionId, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshDrawer = doc.querySelector('[data-section-type="cart-drawer"] cart-drawer');
        var freshInner = freshDrawer && freshDrawer.querySelector('[data-cart-drawer-inner]');
        if (!freshInner) return;

        var isOpen = drawer.classList.contains('is-open');
        var scrollTop = inner.scrollTop;

        // Swap only the children of the sliding panel. The panel element and
        // <cart-drawer> stay, so no open/close animation is triggered.
        inner.innerHTML = freshInner.innerHTML;
        drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
        inner.scrollTop = scrollTop;

        // Re-initialise the panel's accordions (e.g. the PWP Deals widget).
        if (typeof inner.connectedCallback === 'function') {
          inner.collapsibles = inner.querySelectorAll('[data-collapsible]');
          inner.connectedCallback();
        }

        // Re-bind the close button, which lives inside the swapped content.
        drawer.cartDrawerClose = inner.querySelector('[data-cart-drawer-close]');
        if (drawer.cartDrawerClose) {
          drawer.cartDrawerClose.addEventListener('click', function (e) {
            e.preventDefault();
            drawer.closeCartDrawer();
          });
        }

        // Rows render at opacity 0 until the theme's is-animated class is
        // added (the theme does this in requestAnimationFrame, which never
        // runs in a background tab). Force a reflow first so the fade plays.
        void inner.offsetWidth;
        inner.querySelectorAll('[data-animation]').forEach(function (el) {
          el.classList.add('is-animated');
        });

        if (isOpen) {
          // The old <cart-items> unlocks page scroll when it disconnects.
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

          var a11y = window.accessibility && window.accessibility.a11y;
          if (a11y && typeof a11y.trapFocus === 'function') {
            a11y.trapFocus(drawer, { elementToFocus: drawer.cartDrawerClose });
          }
        }

        // Keep the header badge in sync (cart-count listens for this).
        var count = Array.prototype.reduce.call(
          inner.querySelectorAll('input[name="updates[]"]'),
          function (total, input) { return total + (parseInt(input.value, 10) || 0); }, 0
        );
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          bubbles: true, detail: { cartCount: count }
        }));

      })
      .catch(function (error) { console.log(error); });
  }

  // Available everywhere, including the browser console.
  window.refreshCartDrawer = refreshCartDrawer;

  function redrawDrawerForDiscountNinja(event) {
    var cart = event && event.detail && event.detail.data && event.detail.data[0];

    // No redraw is needed when Discount Ninja and Broadcast already show the
    // same cart. This avoids an extra request on ordinary 1 -> 2 updates.
    if (cartMatchesDrawer(cart) || redrawQueued) return;

    redrawQueued = true;
    refreshCartDrawer().then(function () {
      redrawQueued = false;
      notifyDiscountNinja();
    });
  }

  function afterBroadcastBuild() {
    redrawQueued = false;
    notifyDiscountNinja();
  }

  // ---------------------------------------------------------------------
  // la:dn:app:enable  ==  "Discount Ninja, re-render everything you draw"
  //
  // Discount Ninja's enable handler clears its disabled flag and re-renders
  // all of its pricing / badges / promo summary. So it must run AFTER the
  // last change to the drawer content, whoever made that change:
  //   - the theme's own line-item rebuild after a +/- click,
  //   - the full drawer refresh (called by Discount Ninja's "Drawer cart
  //     refresh method" setting = window.refreshCartDrawer(), or by us),
  //   - Discount Ninja's clear/refill of gift lines, which triggers more of
  //     the above.
  //
  // Rather than guessing at event order, watch the drawer content itself:
  // every time THEME content (line items, totals) is swapped in, restart a
  // short quiet timer; when the content has stopped changing, fire once.
  // Discount Ninja's own DOM writes (badges, strikethrough prices, summary)
  // never contain those theme nodes, so they do not restart the timer and
  // cannot cause a loop.
  // ---------------------------------------------------------------------
  var ENABLE_QUIET_MS = 400;   // content must be stable this long before firing
  var ENABLE_LATE_MS = 2500;   // one late re-call: Discount Ninja's own pricing pass
                               // runs ~1.7s after its refresh; this redraws after it
  var enableTimer = null;
  var enableLateTimer = null;
  var THEME_CONTENT = '[data-item], input[name="updates[]"], [data-cart-total], [data-cart-empty], cart-items';

  function fireEnable() {
    document.dispatchEvent(new CustomEvent('la:dn:app:enable'));
  }

  function scheduleEnable() {
    window.clearTimeout(enableTimer);
    window.clearTimeout(enableLateTimer);
    enableTimer = window.setTimeout(fireEnable, ENABLE_QUIET_MS);
    enableLateTimer = window.setTimeout(fireEnable, ENABLE_LATE_MS);
  }

  function nodeHasThemeContent(node) {
    if (node.nodeType !== 1) return false;
    return node.matches(THEME_CONTENT) || Boolean(node.querySelector(THEME_CONTENT));
  }

  function isThemeContentChange(mutation) {
    if (mutation.type !== 'childList') return false;
    var i;
    for (i = 0; i < mutation.addedNodes.length; i++) {
      if (nodeHasThemeContent(mutation.addedNodes[i])) return true;
    }
    for (i = 0; i < mutation.removedNodes.length; i++) {
      if (nodeHasThemeContent(mutation.removedNodes[i])) return true;
    }
    return false;
  }

  function watchDrawerContent() {
    var drawer = document.querySelector('cart-drawer');
     setTimeout(function () {
      document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
      console.log('[Adelais] la:dn:collection:updated (2s after drawer loaded)');
    }, 2000);
    if (!drawer || drawer.__laDnWatched) return;
    drawer.__laDnWatched = true;

    var observer = new MutationObserver(function (mutations) {
      // Only react while the drawer is open; a hidden drawer can wait for
      // theme:cart-drawer:open below.
      if (!drawer.classList.contains('is-open')) return;
      for (var i = 0; i < mutations.length; i++) {
        if (isThemeContentChange(mutations[i])) { scheduleEnable(); return; }
      }
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerContent);
  } else {
    watchDrawerContent();
  }

  // Opening the drawer also re-renders Discount Ninja's content (covers
  // changes made while it was closed, e.g. add-to-cart from a product page).
  document.addEventListener('theme:cart-drawer:open', scheduleEnable);

  document.addEventListener('theme:product:added', afterBroadcastBuild);
  document.addEventListener('theme:cart-drawer:open', notifyDiscountNinja);

  // Tell Discount Ninja the drawer collection changed whenever the drawer
  // opens (cart icon click or add-to-cart) and whenever a product is added
  // while the drawer is already open (no open event fires in that case).
  function notifyCollectionUpdated(e) {
    var trigger = e && e.type;
    window.setTimeout(function () {
      document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
      console.log('[Adelais] la:dn:collection:updated dispatched (1s after ' + trigger + ')');
    }, 1000);
  }
  document.addEventListener('theme:cart-drawer:open', notifyCollectionUpdated);
  document.addEventListener('theme:product:added', notifyCollectionUpdated);
  document.addEventListener(
    'limoniapps:discountninja:drawercart:refreshed',
    redrawDrawerForDiscountNinja
  );
})();
